import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { ApiError, get } from '../../api'
import { useSession } from '../../session'
import { Button, Chip, Icon } from '../../ui'
import {
  ACCESSES,
  activePreset,
  apiQuery,
  applyPreset,
  can,
  DEFAULT_PLANS,
  dayTime,
  endsText,
  filtersFromURL,
  filtersToURL,
  hasHandFilters,
  PAGE_SIZES,
  planTier,
  PRESETS,
  quotaText,
  SOURCES,
  STATUSES,
  accessReason,
  type AdminUserRow,
  type AdminUsersPage,
  type Filters,
  type SortKey,
} from './model'

/*
 * Users in the console (BILL-B6a, design §10.3): the list with plans, access and usage of the
 * period, filtered, sorted and paged by the center; the filters live in the URL so a link can be
 * sent. Built from the system's FilterPresets (presets, dropdowns, cells), MetricsTable /
 * ConsoleNodeTable (the compact table) and UsageMeters (hours as words). On a phone: cards.
 * Usage is the watchdog's last count (usage_computed_at), not counted on request.
 */

const SORT_LABEL: Partial<Record<SortKey, string>> = { email: 'User', last_broadcast: 'Last on air', quota: 'Stream hours', ends: 'Ends' }

export function UsersList() {
  const { user } = useSession()
  const [params, setParams] = useSearchParams()
  const f = useMemo(() => filtersFromURL(params), [params])
  const set = (next: Partial<Filters>) => setParams(filtersToURL({ ...f, page: 0, ...next }), { replace: true })
  const billingRead = can(user, 'billing.read')

  // the search box sends after a pause; the URL keeps what was sent
  const [q, setQ] = useState(f.q)
  useEffect(() => setQ(f.q), [f.q])
  useEffect(() => {
    if (q === f.q) return
    const t = setTimeout(() => set({ q: q.trim() }), 300)
    return () => clearTimeout(t)
  }, [q]) // eslint-disable-line react-hooks/exhaustive-deps

  const query = apiQuery(f).toString()
  const page = useQuery({
    queryKey: ['admin-users-page', query],
    queryFn: () => get<AdminUsersPage>(`/api/admin/users?${query}`),
    placeholderData: (prev) => prev,
    refetchInterval: 30_000,
    retry: false,
  })
  const plans = useQuery({
    queryKey: ['admin-billing-plans'],
    queryFn: () => get<{ plans: { plan_id: string; name: string }[] }>('/api/admin/billing/plans'),
    enabled: billingRead,
    retry: false,
    staleTime: 300_000,
  })
  const billingOff = (page.error instanceof ApiError && page.error.status === 409) || (plans.error instanceof ApiError && plans.error.status === 409)
  const billingFilters = billingRead && !billingOff
  const planIds = useMemo(() => {
    const ids = plans.data?.plans.map((p) => p.plan_id) ?? DEFAULT_PLANS
    return [...new Set(ids)]
  }, [plans.data])

  const d = page.data
  const rows = d?.users ?? []
  const preset = activePreset(f)
  const exportHref = `/api/admin/users.csv?${apiQuery(f, false).toString()}`
  const pages = d ? Math.max(1, Math.ceil(d.total / f.size)) : 1
  const sort = (k: SortKey) => set({ sort: k, desc: f.sort === k ? !f.desc : k !== 'email' })
  const why = billingOff ? 'Billing is off on this center' : 'Needs the billing.read permission'

  return (
    <div className="flex flex-col gap-3" data-testid="users-list">
      <div className="flex items-center gap-2.5">
        <h1 className="text-ui-heading font-medium">Users</h1>
        {d && <span className="sx-mono text-ui-body-sm text-muted" data-testid="users-total">{fmtN(d.total)}</span>}
        <span className="flex-1" />
        <a
          className="sx-btn sm"
          href={exportHref}
          download
          data-testid="users-export"
          aria-disabled={!can(user, 'users.read') || undefined}
          title={can(user, 'users.read') ? 'The list with these filters, as CSV. The export is recorded in the log.' : 'Needs the users.read permission'}
          onClick={(e) => !can(user, 'users.read') && e.preventDefault()}
        >
          <Icon name="download" size={16} />
          Export CSV
        </a>
      </div>
      <div className="sx-panel overflow-hidden !p-0">
        <div className="sx-toolbar flex-wrap" data-testid="users-toolbar">
          <span className="sx-seg" role="radiogroup" aria-label="Quick filters">
            {PRESETS.map((p) => (
              <span
                key={p.id}
                role="radio"
                tabIndex={preset === p.id || (!preset && p.id === 'all') ? 0 : -1}
                aria-checked={preset === p.id}
                aria-disabled={(p.id !== 'all' && p.id !== 'blocked' && !billingFilters) || undefined}
                className={preset === p.id ? 'on' : undefined}
                data-testid={`preset-${p.id}`}
                title={p.id !== 'all' && p.id !== 'blocked' && !billingFilters ? why : undefined}
                onClick={() => (p.id === 'all' || p.id === 'blocked' || billingFilters) && setParams(filtersToURL(applyPreset(f, p.id)), { replace: true })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    ;(e.currentTarget as HTMLElement).click()
                  }
                }}
              >
                {p.label}
                {presetCount(d, p.id) !== undefined && <i className="n">{fmtN(presetCount(d, p.id)!)}</i>}
              </span>
            ))}
          </span>
          <span className="h-5 w-px bg-line-2" aria-hidden />
          <FilterSelect label="Plan" value={f.plan} onChange={(v) => set({ plan: v })} disabled={!billingFilters} why={why} testId="filter-plan"
            options={planIds.map((id) => ({ value: id, label: id.charAt(0).toUpperCase() + id.slice(1) }))} />
          <FilterSelect label="Source" value={f.source} onChange={(v) => set({ source: v as Filters['source'] })} disabled={!billingFilters} why={why} testId="filter-source" options={SOURCES} />
          <FilterSelect label="Subscription" value={f.status} onChange={(v) => set({ status: v })} disabled={!billingFilters} why={why} testId="filter-status" options={STATUSES} />
          <FilterSelect label="Access" value={f.access} onChange={(v) => set({ access: v as Filters['access'] })} testId="filter-access"
            options={ACCESSES} disabledValues={billingFilters ? [] : ['active', 'limited']} why={why} />
          {hasHandFilters(f) && (
            <button type="button" className="whitespace-nowrap text-ui-caption text-accent underline-offset-2 hover:underline" data-testid="filters-reset"
              onClick={() => setParams(filtersToURL(applyPreset(f, 'all')), { replace: true })}>
              Reset filters
            </button>
          )}
          <span className="flex-1" />
          {d && <span className="sx-mono whitespace-nowrap text-ui-caption text-muted" data-testid="users-count">{fmtN(d.total)} {d.total === 1 ? 'user' : 'users'}</span>}
          <label className="sx-input sans !h-7 w-[200px] gap-1.5 !text-ui-caption">
            <Icon name="search" size={16} className="text-muted" />
            <input
              className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted"
              placeholder="E-mail or ID"
              aria-label="Search by e-mail or ID"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="users-search"
            />
          </label>
        </div>
        {page.error && !(page.error instanceof ApiError && page.error.status === 409) ? (
          <p role="alert" className="p-3 text-ui-body-sm text-fail">Users did not load: {(page.error as Error).message}</p>
        ) : !d ? (
          <p className="p-3 text-ui-body-sm text-muted">Loading users…</p>
        ) : rows.length === 0 ? (
          <div className="p-3">
            <div className="sx-empty">
              <div className="ic"><Icon name="user" size={24} /></div>
              <h4>No users match</h4>
              <p>Change the filters or the search. Reset filters shows everyone.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="sx-table compact" data-testid="users-table">
                <thead>
                  <tr>
                    <SortTh k="email" f={f} onSort={sort}>User</SortTh>
                    <th>Plan</th>
                    <th>Access</th>
                    <SortTh k="quota" f={f} onSort={sort} num disabled={!billingFilters}>Stream hours</SortTh>
                    <th className="num">Captions</th>
                    <th className="num">Outputs</th>
                    <SortTh k="last_broadcast" f={f} onSort={sort}>Last on air</SortTh>
                    <th className="num" title="Broadcasts in the last 30 days">30 d</th>
                    <SortTh k="ends" f={f} onSort={sort} disabled={!billingFilters}>Ends</SortTh>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => <Row key={u.id} u={u} />)}
                </tbody>
              </table>
            </div>
            <div className="flex flex-col gap-2 p-2 md:hidden" data-testid="users-cards">
              {rows.map((u) => <UserCardRow key={u.id} u={u} />)}
            </div>
          </>
        )}
        {d && d.total > 0 && (
          <div className="flex items-center gap-2 border-t border-line px-3 py-2">
            <span className="sx-mono text-ui-caption text-muted" data-testid="users-range">
              {fmtN(f.page * f.size + 1)}–{fmtN(Math.min((f.page + 1) * f.size, d.total))} of {fmtN(d.total)}
            </span>
            <span className="flex-1" />
            <label className="sx-select sm relative !h-7 !min-w-0 gap-1 text-ui-caption">
              <span>{f.size} per page</span>
              <Icon name="chevron-down" size={16} />
              <select className="absolute inset-0 cursor-pointer opacity-0" aria-label="Users per page" value={f.size}
                onChange={(e) => set({ size: Number(e.target.value) as Filters['size'] })}>
                {PAGE_SIZES.map((s) => <option key={s} value={s}>{s} per page</option>)}
              </select>
            </label>
            <Button size="sm" disabled={f.page === 0} onClick={() => setParams(filtersToURL({ ...f, page: f.page - 1 }), { replace: true })} data-testid="users-prev">Previous</Button>
            <Button size="sm" disabled={f.page + 1 >= pages} onClick={() => setParams(filtersToURL({ ...f, page: f.page + 1 }), { replace: true })} data-testid="users-next">Next</Button>
          </div>
        )}
      </div>
      {billingOff && <p className="text-ui-caption text-muted">Billing is off on this center (BILLING_MODE=off): no plans or usage to show.</p>}
    </div>
  )
}

function presetCount(d: AdminUsersPage | undefined, id: string): number | undefined {
  return d?.presets[id]
}

const fmtN = (n: number) => n.toLocaleString('en-US').replace(/,/g, ' ')

function SortTh({ k, f, onSort, num, disabled, children }: { k: SortKey; f: Filters; onSort: (k: SortKey) => void; num?: boolean; disabled?: boolean; children: ReactNode }) {
  const on = f.sort === k
  return (
    <th className={num ? 'num' : undefined} aria-sort={on ? (f.desc ? 'descending' : 'ascending') : undefined}>
      <button type="button" className="inline-flex items-center gap-1 uppercase tracking-[inherit] disabled:cursor-default" disabled={disabled} onClick={() => onSort(k)}
        data-testid={`sort-${k}`} title={`Sort by ${SORT_LABEL[k] ?? k}`}>
        {children}
        {on && <span aria-hidden>{f.desc ? '↓' : '↑'}</span>}
      </button>
    </th>
  )
}

/**
 * A dropdown of FilterPresets: muted label, the value in text, "Any" unset; a set filter has
 * data-set (text-soft edge, never accent). The native select over it keeps the keyboard.
 */
function FilterSelect({ label, value, options, onChange, disabled, disabledValues = [], why, testId }: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
  disabled?: boolean
  disabledValues?: string[]
  why?: string
  testId: string
}) {
  const shown = options.find((o) => o.value === value)?.label
  return (
    <label className="sx-select sm relative !h-7 !min-w-0 gap-1 text-ui-caption" data-set={value ? '' : undefined} aria-disabled={disabled || undefined} title={disabled ? why : undefined}>
      <span className="k text-muted">{label}</span>
      {shown ? <b className="font-medium text-text">{shown}</b> : <span className="any text-muted">Any</span>}
      <Icon name="chevron-down" size={16} />
      <select className="absolute inset-0 cursor-pointer opacity-0 disabled:cursor-not-allowed" aria-label={label} value={value} disabled={disabled}
        onChange={(e) => onChange(e.target.value)} data-testid={testId}>
        <option value="">Any</option>
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={disabledValues.includes(o.value)}>{o.label}</option>
        ))}
      </select>
    </label>
  )
}

/** Access (FilterPresets): Active as plain text; LIMITED (warn) and BLOCKED (fail) chips with the reason. */
export function AccessCell({ u }: { u: AdminUserRow }) {
  if (u.status === 'blocked') {
    return (
      <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid="access">
        <Chip state="fail" size="sm">BLOCKED</Chip>
        {u.blocked_reason && <span className="text-ui-caption text-muted">{u.blocked_reason}</span>}
      </span>
    )
  }
  if (u.billing_summary && (u.billing_summary.access !== 'full' || u.billing_summary.has_holds)) {
    return (
      <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid="access">
        <Chip state="warn" size="sm">LIMITED</Chip>
        <span className="text-ui-caption text-muted">{u.billing_summary.access !== 'full' ? accessReason(u.billing_summary.access_reason) : 'Restricted by a hold'}</span>
      </span>
    )
  }
  return <span className="text-text-soft" data-testid="access">Active</span>
}

export function PlanCell({ u }: { u: AdminUserRow }) {
  const b = u.billing_summary
  if (!b) return <span className="text-muted">—</span>
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`sx-plan ${planTier(b.plan_id)}`} data-testid="plan">{b.plan_name}</span>
      {b.source === 'grant' && <span className="sx-tag">GIFT</span>}
    </span>
  )
}

function Hours({ u, k }: { u: AdminUserRow; k: 'stream_hours_month' | 'captions_hours_month' }) {
  if (!u.billing_summary) return <span className="text-muted">—</span>
  const t = quotaText(u.billing_summary.usage[k])
  if (!t) return <span className="text-ui-caption text-muted">Not on plan</span>
  return <span className={t.warn ? 'text-warn' : undefined} data-level={u.billing_summary.usage[k]?.level}>{t.text}</span>
}

function Outputs({ u }: { u: AdminUserRow }) {
  const lim = u.billing_summary?.destinations_limit
  const near = lim != null && lim > 0 && u.destinations / lim >= 0.9 // PlansAndLimits: warn from 90 %
  return (
    <span className={near ? 'text-warn' : undefined}>
      {u.destinations}
      {lim != null && <span className="text-muted"> of {lim}</span>}
    </span>
  )
}

function LastOnAir({ u }: { u: AdminUserRow }) {
  if (u.live) return <Chip state="live" size="sm" still>ON AIR</Chip>
  return <span className="sx-mono text-text-soft">{u.last_live_at ? dayTime(u.last_live_at) : '—'}</span>
}

function Row({ u }: { u: AdminUserRow }) {
  const nav = useNavigate()
  const ends = endsText(u.billing_summary, Date.now(), false)
  return (
    <tr data-testid={`user-row-${u.id}`} className="cursor-pointer" onClick={(e) => !(e.target as HTMLElement).closest('a,button') && nav(`/admin/users/${u.id}`)}>
      <td>
        <div className="nm">
          <Link to={`/admin/users/${u.id}`} className="text-text hover:text-accent hover:underline" data-testid={`user-link-${u.id}`}>{u.email}</Link>
          <span className="sx-mono text-ui-caption text-muted">{u.id}</span>
          {u.billing_summary?.vip && <span className="sx-plan business">VIP</span>}
          {u.role === 'admin' && <span className="sx-tag">ADMIN</span>}
        </div>
      </td>
      <td><PlanCell u={u} /></td>
      <td className="max-w-[240px] !whitespace-normal leading-4"><AccessCell u={u} /></td>
      <td className="num tabular"><Hours u={u} k="stream_hours_month" /></td>
      <td className="num tabular"><Hours u={u} k="captions_hours_month" /></td>
      <td className="num tabular"><Outputs u={u} /></td>
      <td><LastOnAir u={u} /></td>
      <td className="num tabular">{u.broadcasts_30d}</td>
      <td className={`sx-mono ${ends?.warn ? 'text-warn' : ''}`} title={ends?.left !== undefined ? `${ends.left} d left` : undefined}>{ends?.text ?? '—'}</td>
    </tr>
  )
}

/** Phone (< 768): a card per user, as the console's other tables. */
function UserCardRow({ u }: { u: AdminUserRow }) {
  const ends = endsText(u.billing_summary)
  return (
    <Link to={`/admin/users/${u.id}`} className="sx-panel flex flex-col gap-2 !p-3 text-ui-body-sm" data-testid={`user-card-${u.id}`}>
      <span className="flex items-center gap-2">
        <span className="truncate font-medium text-text">{u.email}</span>
        {u.billing_summary?.vip && <span className="sx-plan business">VIP</span>}
      </span>
      <span className="flex items-center gap-2">
        <PlanCell u={u} />
        <span className="flex-1" />
        <AccessCell u={u} />
      </span>
      <span className="sx-mono text-ui-caption"><Hours u={u} k="stream_hours_month" /></span>
      <span className="flex justify-between text-ui-caption text-muted">
        <span>Last on air</span>
        <span className="sx-mono text-text-soft">{u.live ? 'On air now' : dayTime(u.last_live_at)}</span>
      </span>
      {ends && (
        <span className="flex justify-between text-ui-caption text-muted">
          <span>Ends</span>
          <span className={`sx-mono ${ends.warn ? 'text-warn' : 'text-text-soft'}`}>{ends.text}</span>
        </span>
      )}
    </Link>
  )
}
